import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import AdminUserScheduleCard from './AdminUserScheduleCard';
import AdminUserTimeOffCard from './AdminUserTimeOffCard';
import AdminUserCoverageCard from './AdminUserCoverageCard';
import AdminUserMonthlyCalendar from './AdminUserMonthlyCalendar';
import PastTimeOffCard from '../../my-availability/PastTimeOffCard';

const formatRole = (role) => {
  if (!role) return '';
  return role
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
};

function AdminEditUserAvailabilityPanel({
  isOpen,
  onClose,
  userId,
  userName,
  userRole,
  requestId,
  onDataChanged,
}) {
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen && userId) {
      setShouldRender(true);
      setRefreshKey((prev) => prev + 1);
      const timer = setTimeout(() => setIsAnimating(true), 10);
      return () => clearTimeout(timer);
    }
    if (!isOpen) {
      setIsAnimating(false);
      const timer = setTimeout(() => setShouldRender(false), 440);
      return () => clearTimeout(timer);
    }
  }, [isOpen, userId]);

  const handleDataChanged = () => {
    setRefreshKey((prev) => prev + 1);
    onDataChanged?.();
  };

  if (!shouldRender) return null;

  return createPortal(
    <>
      <div
        className={`fixed inset-0 bg-black z-40 transition-opacity duration-300 ${
          isAnimating ? 'bg-opacity-50' : 'bg-opacity-0'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        className={`fixed inset-y-0 right-0 z-50 w-full max-w-4xl bg-gray-50 shadow-2xl transform transition-transform duration-[440ms] ease-in-out md:max-w-5xl lg:max-w-6xl ${
          isAnimating ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ transitionTimingFunction: 'cubic-bezier(0.4, 0.0, 0.2, 1)' }}
        role="dialog"
        aria-modal="true"
        aria-label={`Edit availability${userName ? ` for ${userName}` : ''}`}
      >
        <div className="h-full flex flex-col">
          <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-start justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <button
                type="button"
                onClick={onClose}
                aria-label="Close availability editor"
                className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white shadow border border-gray-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 transition-colors"
              >
                <svg
                  className="w-6 h-6 text-gray-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <div className="min-w-0">
                <h2 className="text-xl font-semibold text-gray-900 truncate">
                  {userName || 'Edit Availability'}
                </h2>
                <p className="text-sm text-gray-500 truncate">
                  {formatRole(userRole)}
                  {userRole ? ' · ' : ''}Edit weekly schedule, time off, and per-day overrides
                </p>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-4 sm:space-y-6">
              {userId ? (
                <>
                  <AdminUserCoverageCard userId={userId} onChange={handleDataChanged} />

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                    <AdminUserScheduleCard userId={userId} onSaved={handleDataChanged} />
                    <AdminUserTimeOffCard
                      userId={userId}
                      requestId={requestId}
                      onChange={handleDataChanged}
                    />
                  </div>

                  <AdminUserMonthlyCalendar
                    userId={userId}
                    refreshKey={refreshKey}
                    onOverrideChanged={handleDataChanged}
                  />

                  <PastTimeOffCard
                    userId={userId}
                    refreshKey={refreshKey}
                    canDelete
                    onChange={handleDataChanged}
                  />
                </>
              ) : (
                <div className="p-8 text-center text-gray-500">No user selected.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}

export default AdminEditUserAvailabilityPanel;
